import React, { useState, useRef, useEffect } from 'react';
import {
  Container,
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Button
} from '@mui/material';
import { Send as SendIcon, ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AIChatPage = () => {
  const [input, setInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);
  const navigate = useNavigate();

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;

    const newMessage = {
      content: input,
      isUser: true,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, newMessage]);
    setInput('');
    setLoading(true);

    console.log('Sending request to Ollama...');

    try {
      const response = await axios.post(
        'http://localhost:11434/api/generate',
        {
          model: "deepseek-r1:8b",
          prompt: `You are a professional underwriting assistant. 
                  Please respond in the same language as the user's question.
                  Question: ${input}`,
          stream: false
        },
        {
          headers: {
            'Content-Type': 'application/json'
          },
          timeout: 30000
        }
      );

      const aiResponse = {
        content: response.data.response,
        isUser: false,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiResponse]);
    } catch (error) {
      console.error('Full error object:', error);
      console.error('Response data:', error.response?.data);
      console.error('Response status:', error.response?.status);
      
      const errorMessage = {
        content: `Local AI service error: ${error.message}\n` +
                 `Status: ${error.response?.status}\n` +
                 `Details: ${JSON.stringify(error.response?.data)}\n\n` +
                 "本地AI服务错误，请检查：\n" +
                 "1. Ollama服务是否运行\n" +
                 "2. deepseek模型是否已加载\n" +
                 "3. 端口11434是否可访问",
        isUser: false,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="xl" sx={{ 
      minHeight: 'calc(100vh - 160px)',
      display: 'flex', 
      flexDirection: 'column', 
      gap: 2,
      p: 2,
      backgroundColor: '#fff',
      borderRadius: 2,
      boxShadow: 1,
      mt: 6,
      mb: 2
    }}>
      <Paper elevation={0} sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: 3,
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Hero Section */}
        <Box sx={{ 
          pt: 4,
          pb: 3,
          textAlign: 'center',
          position: 'relative',
          borderBottom: '1px solid #e0e0e0',
          mb: 4
        }}>
          <Button
            variant="outlined"
            onClick={() => navigate(-1)}
            startIcon={<ArrowBackIcon />}
            sx={{
              position: 'absolute',
              left: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#1976d2',
              borderColor: '#1976d2',
              '&:hover': {
                borderColor: '#1565c0',
                backgroundColor: 'rgba(25, 118, 210, 0.04)'
              }
            }}
          >
            Back
          </Button>

          <Typography 
            variant="h4" 
            sx={{ 
              color: '#1976d2',
              fontWeight: 600,
              mb: 1
            }}
          >
            UW Assistant
          </Typography>
          <Typography 
            variant="subtitle1" 
            sx={{ 
              color: '#666',
              maxWidth: '600px',
              mx: 'auto',
              fontStyle: 'italic'
            }}
          >
            AI-powered underwriting support
          </Typography>

          {/* 右侧功能按钮（保持布局对称） */}
          <Button
            variant="contained"
            sx={{
              position: 'absolute',
              right: 0,
              top: '50%',
              transform: 'translateY(-50%)',
              backgroundColor: '#1976d2',
              '&:hover': {
                backgroundColor: '#1565c0',
              },
            }}
          >
            New Chat
          </Button>
        </Box>

        <Box sx={{ 
          flex: 1, 
          overflowY: 'auto', 
          p: 1.5,
          backgroundColor: '#fafafa',
          maxHeight: 'calc(100vh - 360px)'
        }}>
          <List sx={{ pb: 8 }}>
            {messages.map((message, index) => (
              <ListItem key={index} sx={{ 
                justifyContent: message.isUser ? 'flex-end' : 'flex-start',
                alignItems: 'flex-start',
                py: 2
              }}>
                <Paper elevation={0} sx={{ 
                  maxWidth: '85%',
                  p: 1.5,
                  backgroundColor: message.isUser ? '#1976d2' : '#e3f2fd',
                  borderRadius: message.isUser 
                    ? '16px 16px 4px 16px' 
                    : '16px 16px 16px 4px'
                }}>
                  <Typography variant="body2" sx={{ 
                    color: message.isUser ? 'white' : 'text.primary',
                    lineHeight: 1.4
                  }}>
                    {message.content}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    display: 'block',
                    mt: 1,
                    color: message.isUser ? 'rgba(255,255,255,0.7)' : 'text.secondary',
                    textAlign: 'right'
                  }}>
                    {new Date(message.timestamp).toLocaleTimeString()}
                  </Typography>
                </Paper>
              </ListItem>
            ))}
            {loading && (
              <ListItem sx={{ justifyContent: 'flex-start' }}>
                <CircularProgress size={24} sx={{ mr: 2 }} />
                <Typography variant="body2" color="text.secondary">
                  UW Assistant is thinking...
                </Typography>
              </ListItem>
            )}
            <div ref={messagesEndRef} />
          </List>
        </Box>

        <Box component="form" onSubmit={handleSubmit} sx={{ 
          p: 2, 
          borderTop: '1px solid #e0e0e0',
          backgroundColor: 'white',
          position: 'sticky',
          bottom: 0,
          zIndex: 1
        }}>
          <TextField
            fullWidth
            variant="outlined"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Ask about insurance policy terms, coverage details, or underwriting requirements"
            InputProps={{
              endAdornment: (
                <IconButton 
                  type="submit" 
                  disabled={loading}
                  sx={{ color: '#1976d2' }}
                >
                  <SendIcon />
                </IconButton>
              ),
              sx: {
                borderRadius: '28px',
                '& fieldset': {
                  borderColor: '#e0e0e0'
                }
              }
            }}
          />
        </Box>
      </Paper>
    </Container>
  );
};

export default AIChatPage; 